import type { LoginInfo, LoginOrganisationInfo, Partner, PartnerVariant } from "@/types/LoginInfo";

/**
 * STIC-16004: does an organisation do business with Sensolus directly, or
 * through a channel partner? Mirror of the backend `DirectBusinessRule` - keep
 * the two in step.
 *
 *   login level   variant of ...        purchase orders / support / billing
 *   PARTNER       itself                CHANNEL_PARTNER, MULTI_ORG: yes
 *                                       SENSOLUS (Sensolus' own dummy partner),
 *                                       MULTI_ORG_INDIRECT: no
 *   NORMAL        its parent partner    SENSOLUS: yes (direct customer)
 *                                       any other variant: no
 *   SYSTEM        -                     no own entry in the menu; the backend
 *                                       lets System read everything
 *
 * Replaces the `show_invoicing` feature key, which had to be granted by hand
 * and ended up on indirect customers by mistake.
 */

/** The minimum an organisation has to carry for these rules. */
export interface DirectBusinessOrg {
  organisationType?: string;
  partner?: Pick<Partner, "partnerVariant" | "commercialPartner"> | null;
  /** EU / US / WORLD price list, set on partner orgs. Only the partner Quotes look at it. */
  partnerPriceList?: string;
}

const variantOf = (org: DirectBusinessOrg | null | undefined): PartnerVariant | undefined =>
  org?.partner?.partnerVariant;

/**
 * Purchase orders, Support tickets, Billing and subscriptions for the
 * organisation itself (own level). System is deliberately `false` here: those
 * pages are the organisation's side of the business with Sensolus, and System
 * has no counterpart of its own. Use {@link canReadDirectBusinessOf} for the
 * cross-level view.
 */
export const hasDirectBusinessWithSensolus = (org: DirectBusinessOrg | null | undefined): boolean => {
  const variant = variantOf(org);
  switch (org?.organisationType) {
    case "PARTNER":
      return variant === "CHANNEL_PARTNER" || variant === "MULTI_ORG";
    case "NORMAL":
      return variant === "SENSOLUS";
    default:
      return false;
  }
};

/**
 * Whether the login may open the direct-business pages of the organisation it
 * is looking at: its own organisation at own level, or a browsed one at cross
 * level. A System login reads everything; every other login is bound to its
 * own organisation's answer.
 */
export const canReadDirectBusinessOf = (
  loginInfo: LoginInfo | null | undefined,
  org: DirectBusinessOrg | null | undefined
): boolean => {
  if (loginInfo?.organisation?.organisationType === "SYSTEM") {
    return true;
  }
  return hasDirectBusinessWithSensolus(org);
};

/**
 * The login's own organisation, shaped for the rules above. `LoginInfo` carries
 * the partner next to the organisation rather than inside it.
 */
export const loginOrg = (loginInfo: LoginInfo | null | undefined): DirectBusinessOrg | null => {
  const organisation: LoginOrganisationInfo | undefined = loginInfo?.organisation;
  if (!organisation) {
    return null;
  }
  return {
    organisationType: organisation.organisationType,
    partner: loginInfo?.partner ?? null,
    partnerPriceList: organisation.partnerPriceList,
  };
};

/** Own-level shortcut: does the logged-in organisation deal with Sensolus directly? */
export const loginHasDirectBusiness = (loginInfo: LoginInfo | null | undefined): boolean =>
  hasDirectBusinessWithSensolus(loginOrg(loginInfo));

/**
 * The presales tools (Demos, Battery calculator, partner Quotes) belong to
 * commercial channel partners: variant CHANNEL_PARTNER with the commercial flag
 * (VAR with contract signed, or the commercial override). Quotes add the EU
 * price list on top, see `quoteAccess.ts`.
 */
export const isCommercialChannelPartner = (org: DirectBusinessOrg | null | undefined): boolean =>
  org?.organisationType === "PARTNER" &&
  variantOf(org) === "CHANNEL_PARTNER" &&
  !!org?.partner?.commercialPartner;

/** Own-level shortcut for the presales tools. */
export const loginIsCommercialChannelPartner = (loginInfo: LoginInfo | null | undefined): boolean =>
  isCommercialChannelPartner(loginOrg(loginInfo));

/**
 * The partner Quotes: a commercial channel partner on the EU price list (the
 * EU limitation is temporary, STIC-15133). Marks 1, 2 and 3 of the matrix.
 */
export const isCommercialEuChannelPartner = (org: DirectBusinessOrg | null | undefined): boolean =>
  isCommercialChannelPartner(org) && org?.partnerPriceList === "EU";

/** Own-level shortcut for the partner Quotes. */
export const loginIsCommercialEuChannelPartner = (loginInfo: LoginInfo | null | undefined): boolean =>
  isCommercialEuChannelPartner(loginOrg(loginInfo));
